import { defineStore } from 'pinia'
import type Star from '@/types/Star'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore'

export const useStarStore = defineStore('starStore', {
  state: () => ({
    stars: new Map<number, Star>()
  }),
  getters: {
    findStarById:
      (state) =>
      (id: number): Star | undefined => {
        return state.stars.get(id)
      },
    findStarByCoordinates:
      (state) =>
      (xPos: number, yPos: number): Star | undefined => {
        return Array.from(state.stars.values()).find(
          (star) => star.xPos === xPos && star.yPos === yPos
        )
      },
    getAllStars: (state): Star[] => {
      return Array.from(state.stars.values())
    }
  },
  actions: {
    updateStarById(id: number, data: Star) {
      this.stars.set(id, data)
    },
    deleteStarById(id: number) {
      if (this.stars.has(id)) {
        this.stars.delete(id)
      }
    },
    recalculateStarOwnership(starId: number): number {
      const mapCoordinateStore = useMapCoordinateStore()

      // Get all planets belonging to this star using indexed lookup (O(1) instead of O(n))
      const starPlanets = mapCoordinateStore.getPlanetsForStar(starId)
      const planetsByOwner = new Map<number, number>()

      // Count planets by owner
      for (const planet of starPlanets) {
        const ownerId = planet.playerId
        const currentCount = planetsByOwner.get(ownerId) || 0
        planetsByOwner.set(ownerId, currentCount + 1)
      }

      // Determine the majority owner (excluding player 0 from competition)
      let majorityPlayerId = 0
      let maxPlanetCount = 0

      for (const [playerId, count] of planetsByOwner.entries()) {
        // Skip player 0 - they should only win if no other players have planets
        if (playerId === 0) {
          continue
        }

        if (count > maxPlanetCount) {
          majorityPlayerId = playerId
          maxPlanetCount = count
        } else if (count === maxPlanetCount && playerId < majorityPlayerId) {
          // On tie, use lowest playerId (but not 0)
          majorityPlayerId = playerId
        }
      }

      // If no real players have planets, check if player 0 has any
      if (majorityPlayerId === 0 && planetsByOwner.has(0)) {
        maxPlanetCount = planetsByOwner.get(0) || 0
      }

      // Update the star's playerId
      const star = this.stars.get(starId)
      if (star) {
        star.playerId = majorityPlayerId
        this.stars.set(starId, star)
      }

      return majorityPlayerId
    }
  }
})
